import { computed, defineComponent, onBeforeUnmount, onMounted, onUpdated, shallowRef, Text, watch, watchEffect, } from 'vue';
import Wave from '../_util/wave';
import buttonProps from './buttonTypes';
import { flattenChildren, initDefaultProps } from '../_util/props-util';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { useInjectDisabled } from '../config-provider/DisabledContext';
import devWarning from '../vc-util/devWarning';
import LoadingIcon from './LoadingIcon';
import useStyle from './style';
import { GroupSizeContext } from './button-group';
import { useCompactItemContext } from '../space/Compact';
import { ButtonTypeMap, isUnBorderedButtonVariant, } from './buttonHelpers';
const rxTwoCNChar = /^[\u4e00-\u9fa5]{2}$/;
const isTwoCNChar = rxTwoCNChar.test.bind(rxTwoCNChar);
function isUnBorderedButtonType(type) {
    return type === 'text' || type === 'link';
}
export { buttonProps };
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'AButton',
    inheritAttrs: false,
    __ANT_BUTTON: true,
    // No default `type` — undefined allows ConfigProvider `button.color`/`variant` (antd ≥ 5.25)
    props: initDefaultProps(buttonProps(), {}),
    slots: Object,
    // emits: ['click', 'mousedown'],
    setup(props, { slots, attrs, emit, expose }) {
        const { prefixCls, autoInsertSpaceInButton, direction, size, configProvider } = useConfigInject('btn', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const groupSizeContext = GroupSizeContext.useInject();
        const disabledContext = useInjectDisabled();
        const mergedDisabled = computed(() => { var _a; return (_a = props.disabled) !== null && _a !== void 0 ? _a : disabledContext.value; });
        const buttonNodeRef = shallowRef(null);
        const delayTimeoutRef = shallowRef(undefined);
        let isNeedInserted = false;
        const innerLoading = shallowRef(false);
        const hasTwoCNChar = shallowRef(false);
        const autoInsertSpace = computed(() => {
            var _a, _b, _c, _d;
            return (_d = (_a = props.autoInsertSpace) !== null && _a !== void 0 ? _a : (_c = (_b = configProvider.button) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.autoInsertSpace) !== null && _d !== void 0 ? _d : autoInsertSpaceInButton.value !== false;
        });
        const { compactSize, compactItemClassnames } = useCompactItemContext(prefixCls, direction);
        // =============== Update Loading ===============
        const loadingOrDelay = computed(() => typeof props.loading === 'object' && props.loading.delay
            ? props.loading.delay || true
            : !!props.loading);
        watch(loadingOrDelay, val => {
            clearTimeout(delayTimeoutRef.value);
            if (typeof loadingOrDelay.value === 'number') {
                delayTimeoutRef.value = setTimeout(() => {
                    innerLoading.value = val;
                }, loadingOrDelay.value);
            }
            else {
                innerLoading.value = val;
            }
        }, {
            immediate: true,
        });
        const mergedIconPlacement = computed(() => { var _a, _b; return (_b = (_a = props.iconPlacement) !== null && _a !== void 0 ? _a : props.iconPosition) !== null && _b !== void 0 ? _b : 'start'; });
        const mergedColorVariant = computed(() => {
            var _a;
            const { color, variant, type, danger } = props;
            if (color && variant) {
                return [color, variant];
            }
            // Partial color/variant: fall back through type map
            if (color || variant) {
                const mapped = ButtonTypeMap[type || 'default'] || [];
                const mappedColor = (danger ? 'danger' : color || mapped[0] || 'default');
                const mappedVariant = (variant || mapped[1] || 'outlined');
                return [mappedColor, mappedVariant];
            }
            // Explicit type / danger keep legacy classes (blocks ConfigProvider, same as antd)
            if (type || danger) {
                return null;
            }
            // ConfigProvider button.color + button.variant
            const buttonConfig = (_a = configProvider.button) === null || _a === void 0 ? void 0 : _a.value;
            if ((buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.color) && (buttonConfig === null || buttonConfig === void 0 ? void 0 : buttonConfig.variant)) {
                return [buttonConfig.color, buttonConfig.variant];
            }
            return null;
        });
        const classes = computed(() => {
            const { type, shape = 'default', ghost, block, danger = false } = props;
            const pre = prefixCls.value;
            const mergedType = type || 'default';
            const sizeClassNameMap = { large: 'lg', small: 'sm', middle: undefined };
            const sizeFullname = compactSize.value || (groupSizeContext === null || groupSizeContext === void 0 ? void 0 : groupSizeContext.size) || size.value;
            const sizeCls = sizeFullname ? sizeClassNameMap[sizeFullname] || '' : '';
            const colorVariant = mergedColorVariant.value;
            const useColorVariant = !!colorVariant;
            const mergedColor = colorVariant === null || colorVariant === void 0 ? void 0 : colorVariant[0];
            const mergedVariant = colorVariant === null || colorVariant === void 0 ? void 0 : colorVariant[1];
            const unbordered = useColorVariant
                ? isUnBorderedButtonVariant(mergedVariant)
                : isUnBorderedButtonType(mergedType);
            return [
                compactItemClassnames.value,
                {
                    [hashId.value]: true,
                    [`${pre}`]: true,
                    [`${pre}-${shape}`]: shape !== 'default' && shape,
                    // Legacy type classes when color/variant API is not used
                    [`${pre}-${mergedType}`]: !useColorVariant,
                    // Isolated color/variant classes to avoid clashing with type styles
                    [`${pre}-color-${mergedColor}`]: useColorVariant && !!mergedColor,
                    [`${pre}-variant-${mergedVariant}`]: useColorVariant && !!mergedVariant,
                    [`${pre}-${sizeCls}`]: sizeCls,
                    [`${pre}-loading`]: innerLoading.value,
                    [`${pre}-background-ghost`]: ghost && !unbordered,
                    [`${pre}-two-chinese-chars`]: hasTwoCNChar.value && autoInsertSpace.value,
                    [`${pre}-block`]: block,
                    [`${pre}-dangerous`]: !useColorVariant && !!danger,
                    [`${pre}-rtl`]: direction.value === 'rtl',
                    [`${pre}-icon-end`]: mergedIconPlacement.value === 'end',
                },
            ];
        });
        const fixTwoCNChar = () => {
            // Fix for HOC usage like <FormatMessage />
            const node = buttonNodeRef.value;
            if (!node || autoInsertSpace.value === false) {
                return;
            }
            const buttonText = node.textContent;
            if (isNeedInserted && isTwoCNChar(buttonText)) {
                if (!hasTwoCNChar.value) {
                    hasTwoCNChar.value = true;
                }
            }
            else if (hasTwoCNChar.value) {
                hasTwoCNChar.value = false;
            }
        };
        const handleClick = (event) => {
            // https://github.com/ant-design/ant-design/issues/30207
            if (innerLoading.value || mergedDisabled.value) {
                event.preventDefault();
                return;
            }
            emit('click', event);
        };
        const handleMousedown = (event) => {
            emit('mousedown', event);
        };
        const insertSpace = (child, needInserted) => {
            const SPACE = needInserted ? ' ' : '';
            if (child.type === Text) {
                let text = child.children.trim();
                if (isTwoCNChar(text)) {
                    text = text.split('').join(SPACE);
                }
                return <span>{text}</span>;
            }
            return child;
        };
        watchEffect(() => {
            const colorVariant = mergedColorVariant.value;
            const unbordered = colorVariant
                ? isUnBorderedButtonVariant(colorVariant[1])
                : isUnBorderedButtonType(props.type);
            devWarning(!(props.ghost && unbordered), 'Button', "`link` or `text` button can't be a `ghost` button.");
            devWarning(!(props.iconPosition !== undefined), 'Button', '`iconPosition` is deprecated. Please use `iconPlacement` instead.');
        });
        onMounted(fixTwoCNChar);
        onUpdated(fixTwoCNChar);
        onBeforeUnmount(() => {
            delayTimeoutRef.value && clearTimeout(delayTimeoutRef.value);
        });
        const focus = () => {
            var _a;
            (_a = buttonNodeRef.value) === null || _a === void 0 ? void 0 : _a.focus();
        };
        const blur = () => {
            var _a;
            (_a = buttonNodeRef.value) === null || _a === void 0 ? void 0 : _a.blur();
        };
        expose({
            focus,
            blur,
        });
        return () => {
            var _a, _b;
            const { icon = (_a = slots.icon) === null || _a === void 0 ? void 0 : _a.call(slots) } = props;
            const children = flattenChildren((_b = slots.default) === null || _b === void 0 ? void 0 : _b.call(slots));
            const colorVariant = mergedColorVariant.value;
            const unbordered = colorVariant
                ? isUnBorderedButtonVariant(colorVariant[1])
                : isUnBorderedButtonType(props.type);
            isNeedInserted = children.length === 1 && !icon && !unbordered;
            const { htmlType, href, title, target } = props;
            const iconType = innerLoading.value ? 'loading' : icon;
            const buttonProps = Object.assign(Object.assign({}, attrs), { title, disabled: mergedDisabled.value, class: [
                    classes.value,
                    attrs.class,
                    { [`${prefixCls.value}-icon-only`]: children.length === 0 && !!iconType },
                ], onClick: handleClick, onMousedown: handleMousedown });
            // https://github.com/vueComponent/ant-design-vue/issues/4930
            if (!mergedDisabled.value) {
                delete buttonProps.disabled;
            }
            const loadingIcon = typeof props.loading === 'object' && props.loading
                ? props.loading.icon
                : undefined;
            const iconNode = icon && !innerLoading.value ? (icon) : (<LoadingIcon existIcon={!!icon} prefixCls={prefixCls.value} loading={!!innerLoading.value} icon={loadingIcon}/>);
            const kids = children.map(child => insertSpace(child, isNeedInserted && autoInsertSpace.value));
            const isRTL = direction.value === 'rtl';
            const iconFirst = (mergedIconPlacement.value === 'start' && !isRTL) ||
                (mergedIconPlacement.value === 'end' && isRTL);
            const contentNodes = iconFirst ? (<>
          {iconNode}
          {kids}
        </>) : (<>
          {kids}
          {iconNode}
        </>);
            if (href !== undefined) {
                return wrapSSR(<a {...buttonProps} href={href} target={target} ref={buttonNodeRef}>
            {contentNodes}
          </a>);
            }
            let buttonNode = (<button {...buttonProps} ref={buttonNodeRef} type={htmlType}>
          {contentNodes}
        </button>);
            if (!unbordered) {
                buttonNode = (<Wave ref="wave" disabled={!!innerLoading.value}>
            {buttonNode}
          </Wave>);
            }
            return wrapSSR(buttonNode);
        };
    },
});
